/* eslint-disable  @typescript-eslint/no-explicit-any */
import { getFromLocalStorage } from '@/shared/localStorage/localStorageRepository'
import { postWithAuth } from '@/api/httpRequests'
import { getErrorMessage } from '@/shared/lib'
import { LAST_ACTUAL_PROFILE } from '@/shared/constants'
import { deviceId } from '@/api/streemiq/apiConstantsStreemIQ'
import BaseChannel from '@/shared/classes/tv/BaseChannel'
import { getBootstraps } from '@/clients/streamiq/shared/lib'

interface TstvConfig {
    catchUpDurationInDays: number
    npvr: boolean
    restart: boolean
    pause: boolean
    catchUp: boolean
}

interface SourceImage {
    orientation: string
    id: string
    size: string
    url: string
}

interface Image {
    url: string
    size: string
    orientation: string
}

export interface StreamIQChannelData {
    tstvConfig: Partial<TstvConfig>
    externalId: string
    sourceImages: SourceImage[]
    id: string
    genres: string[]
    feedType: string
    name: string
    images: Image[]
    serviceName: string
    type: string
    icon: string
    lcn: string
    drm: number
    drmId: string
    origin: number
    service: number
    uri: string
    cat: string[]
    list: string[]
    licenseProxyUri: string
    preAuthTokenUri: string
    certificateUri: string | null
}

export class StreamIQChannel
    extends BaseChannel
    implements StreamIQChannelData
{
    // Properties from StreamIQChannelData
    tstvConfig: Partial<TstvConfig>
    externalId: string
    sourceImages: SourceImage[]
    genres: string[]
    feedType: string
    serviceName: string
    type: string
    icon: string
    lcn: string
    drm: number
    drmId: string
    origin: number
    service: number
    uri: string
    cat: string[]
    list: string[]
    licenseProxyUri: string
    preAuthTokenUri: string
    certificateUri: string | null
    images: Image[]

    constructor(channelData: StreamIQChannelData) {
        // Call the parent (Channel) constructor
        super({
            id: channelData.id,
            name: channelData.name,
            icon: channelData.icon || channelData.images[0]?.url || '',
            lcn: channelData.lcn,
            drm: channelData.drm,
            drmId: channelData.drmId,
            origin: channelData.origin,
            service: channelData.service,
            uri: '',
            cat: channelData.cat,
            list: channelData.list,
            licenseProxyUri: channelData.licenseProxyUri,
            preAuthTokenUri: channelData.preAuthTokenUri,
            certificateUri: channelData.certificateUri,
        })

        // StreamIQChannel specific properties
        this.tstvConfig = channelData.tstvConfig
        this.externalId = channelData.externalId
        this.sourceImages = channelData.sourceImages
        this.genres = channelData.genres
        this.feedType = channelData.feedType
        this.serviceName = channelData.serviceName
        this.type = channelData.type
        this.icon = channelData.icon
        this.lcn = channelData.lcn
        this.drm = channelData.drm
        this.drmId = channelData.drmId
        this.origin = channelData.origin
        this.service = channelData.service
        this.uri = channelData.uri
        this.cat = channelData.cat
        this.list = channelData.list
        this.licenseProxyUri = channelData.licenseProxyUri
        this.preAuthTokenUri = channelData.preAuthTokenUri
        this.certificateUri = channelData.certificateUri
        this.images = channelData.images
    }

    async initializeUri(channelData: StreamIQChannel) {
        this.uri = await StreamIQChannel.getStreamIQChannelUri(channelData)
    }

    static fromJson(data: any): StreamIQChannel {
        return new StreamIQChannel(data)
    }

    static serialize(channel: StreamIQChannel) {
        return {
            ...BaseChannel.serialize(channel),
            tstvConfig: channel.tstvConfig,
            externalId: channel.externalId,
            sourceImages: channel.sourceImages,
            genres: channel.genres,
            feedType: channel.feedType,
            serviceName: channel.serviceName,
            type: channel.type,
            icon: channel.icon,
            lcn: channel.lcn,
            drm: channel.drm,
            drmId: channel.drmId,
            origin: channel.origin,
            service: channel.service,
            uri: channel.uri,
            cat: channel.cat,
            list: channel.list,
            licenseProxyUri: channel.licenseProxyUri,
            preAuthTokenUri: channel.preAuthTokenUri,
            certificateUri: channel.certificateUri,
        }
    }

    static deserialize(data: any): StreamIQChannel {
        return new StreamIQChannel(data)
    }

    static getChannelURL = async (selectedChannel: StreamIQChannelData) => {
        const bootstraps = getBootstraps()

        const url = bootstraps?.services?.['session-service'].session
        const actualProfile = getFromLocalStorage(LAST_ACTUAL_PROFILE)
        try {
            const response = await postWithAuth(
                url,
                false,
                JSON.stringify({
                    streamingProtocol: 'hls',
                    deviceFingerprint: deviceId,
                    profileId: actualProfile,
                    reference: {
                        id: selectedChannel?.id || '',
                        type: 'channel',
                        serviceName: 'epg-service',
                    },
                })
            )
            if (response) return response.playInfo.url
        } catch (error) {
            getErrorMessage('getChannelURL', error)
            return ''
        }
    }
    static getStreamIQChannelUri = async (
        selectedChannel: StreamIQChannelData
    ) => {
        try {
            return await this.getChannelURL(selectedChannel)
        } catch (error) {
            console.log('error', error)
        }
    }
}
